//srs/pages/Clientes.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";

import { Link } from "react-router-dom";
import {
  AlertTriangle,
  BadgeCheck,
  Building2,
  FileClock,
  FileSpreadsheet,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import * as XLSX from "xlsx";

import ModalSemPermissao from "../components/ModalSemPermissao";

import "./Clientes.css";

function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filtros, setFiltros] = useState({
    busca: "",
    tipo_pessoa: "",
    status: "",
    perfil: "",
    documento: "",
    empresa: "",
    cidade: "",
    uf: "",
  });

  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 50;

  const usuario = JSON.parse(localStorage.getItem("usuario"));

  const podeVerClientes =
    usuario?.tipo === "admin" ||
    usuario?.permissoes?.some((p) => p.rota === "clientes" && p.pode_ler);

  const podeExportarClientes =
    usuario?.tipo === "admin" ||
    usuario?.permissoes?.some((p) => p.rota === "clientes" && p.pode_exportar);

  const podeInserirClientes =
    usuario?.tipo === "admin" ||
    usuario?.permissoes?.some((p) => p.rota === "clientes" && p.pode_inserir);

  const podeEditarClientes =
    usuario?.tipo === "admin" ||
    usuario?.permissoes?.some((p) => p.rota === "clientes" && p.pode_editar);

  useEffect(() => {
    carregarClientes();
  }, []);

  const carregarClientes = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get("/clientes");

      const idsUnicos = new Set();
      const unicos = Array.isArray(data)
        ? data.filter((cliente) => {
            if (idsUnicos.has(cliente.id)) return false;
            idsUnicos.add(cliente.id);
            return true;
          })
        : [];

      setClientes(unicos);
    } catch (err) {
      console.error("Erro ao buscar clientes:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({ ...prev, [name]: value }));
    setPaginaAtual(1);
  };

  const normalizeText = (text) =>
    (text || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\w\s@.-]/g, "")
      .toLowerCase()
      .trim();

  const apenasNumeros = (value) => String(value || "").replace(/\D/g, "");

  const isPerfilConfirmado = (cliente) =>
    Number(cliente.perfil_atualizado) === 1 &&
    Number(cliente.documento_validado) === 1 &&
    Number(cliente.termo_pontos_aceito) === 1 &&
    Number(cliente.forcar_atualizacao_perfil) !== 1;

  const clientesFiltrados = useMemo(() => {
    return clientes.filter((cliente) => {
      const busca = normalizeText(filtros.busca);
      const buscaNumeros = apenasNumeros(filtros.busca);

      const textoCliente = normalizeText(
        [
          cliente.id,
          cliente.nome,
          cliente.email,
          cliente.telefone,
          cliente.cpf_cnpj,
          cliente.cpf,
          cliente.cnpj,
          cliente.cnpj_empresa,
          cliente.nome_empresa,
          cliente.identificador_conta_pj,
          cliente.cidade,
          cliente.estado,
        ].join(" ")
      );

      const documentoCliente = apenasNumeros(
        `${cliente.cpf_cnpj || ""} ${cliente.cpf || ""} ${cliente.cnpj || ""}`
      );

      const confirmado = isPerfilConfirmado(cliente);
      const documentoValidado = Number(cliente.documento_validado) === 1;

      return (
        (!busca ||
          textoCliente.includes(busca) ||
          (buscaNumeros && documentoCliente.includes(buscaNumeros))) &&
        (!filtros.tipo_pessoa || cliente.tipo_pessoa === filtros.tipo_pessoa) &&
        (!filtros.status || cliente.status === filtros.status) &&
        (!filtros.perfil ||
          (filtros.perfil === "confirmado" && confirmado) ||
          (filtros.perfil === "pendente" && !confirmado)) &&
        (!filtros.documento ||
          (filtros.documento === "validado" && documentoValidado) ||
          (filtros.documento === "pendente" && !documentoValidado)) &&
        (!filtros.empresa ||
          normalizeText(cliente.nome_empresa).includes(
            normalizeText(filtros.empresa)
          )) &&
        (!filtros.cidade ||
          normalizeText(cliente.cidade).includes(normalizeText(filtros.cidade))) &&
        (!filtros.uf ||
          normalizeText(cliente.estado).includes(normalizeText(filtros.uf)))
      );
    });
  }, [clientes, filtros]);

  const totalPaginas = Math.max(
    1,
    Math.ceil(clientesFiltrados.length / itensPorPagina)
  );

  const clientesPagina = clientesFiltrados.slice(
    (paginaAtual - 1) * itensPorPagina,
    paginaAtual * itensPorPagina
  );

  const resumo = useMemo(() => {
    const total = clientes.length;
    const ativos = clientes.filter((c) => c.status === "ativo").length;
    const confirmados = clientes.filter(isPerfilConfirmado).length;
    const pendentes = total - confirmados;

    return { total, ativos, confirmados, pendentes };
  }, [clientes]);

  const formatarData = (data) => {
    if (!data) return "—";

    try {
      return new Date(String(data).replace(/-/g, "/")).toLocaleDateString(
        "pt-BR"
      );
    } catch {
      return "—";
    }
  };

  const formatarPontos = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    });

  const exportarExcel = () => {
    const dadosExportar = clientesFiltrados.map((c) => ({
      ID: c.id,
      Nome: c.nome,
      Email: c.email,
      Telefone: c.telefone,
      "Tipo de Conta": c.tipo_pessoa === "juridica" ? "PJ" : "PF",
      "CPF/CNPJ": c.cpf_cnpj,
      "CNPJ da Empresa": c.cnpj_empresa,
      "Razão Social": c.nome_empresa,
      "Identificador PJ": c.identificador_conta_pj,
      Cidade: c.cidade,
      UF: c.estado,
      Status: c.status,
      Perfil: c.perfil,
      "Perfil Confirmado": isPerfilConfirmado(c) ? "Sim" : "Não",
      "Documento Validado": Number(c.documento_validado) === 1 ? "Sim" : "Não",
      "Data Aceite": c.data_aceite_termo
        ? new Date(c.data_aceite_termo).toLocaleString("pt-BR")
        : "",
      IP: c.ip_aceite_termo || "",
      "Total de Logs": c.total_logs || 0,
      "Pontos Disponíveis": c.disponivel || 0,
      "Data de Cadastro": c.data_cadastro
        ? new Date(c.data_cadastro).toLocaleDateString("pt-BR")
        : "",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dadosExportar);
    XLSX.utils.book_append_sheet(wb, ws, "Clientes");
    XLSX.writeFile(wb, "clientes_tikpontos.xlsx");
  };

  if (!podeVerClientes) {
    return <ModalSemPermissao onClose={() => (window.location.href = "/")} />;
  }

  return (
    <div className="clientesAdmin-page">
      <div className="clientesAdmin-topbar">
        <div>
          <span className="clientesAdmin-kicker">Área administrativa</span>
          <h2>Clientes</h2>
          <p>
            Consulte cadastros, acompanhe confirmação de perfil e acesse a
            auditoria das alterações.
          </p>
        </div>

        <div className="clientesAdmin-actions">
          {podeExportarClientes && (
            <button className="clientesAdmin-btn secondary" onClick={exportarExcel}>
              <FileSpreadsheet size={16} />
              Exportar Excel
            </button>
          )}

          {podeInserirClientes && (
            <Link to="/clientes/novo" className="clientesAdmin-btn primary">
              <Plus size={16} />
              Novo Cliente
            </Link>
          )}
        </div>
      </div>

      <div className="clientesAdmin-cards">
        <div className="clientesAdmin-card">
          <span>Total de clientes</span>
          <strong>{resumo.total.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="clientesAdmin-card ativo">
          <span>Ativos</span>
          <strong>{resumo.ativos.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="clientesAdmin-card confirmado">
          <span>Perfis confirmados</span>
          <strong>{resumo.confirmados.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="clientesAdmin-card pendente">
          <span>Pendentes</span>
          <strong>{resumo.pendentes.toLocaleString("pt-BR")}</strong>
        </div>
      </div>

      <div className="clientesAdmin-filtros">
        <div className="clientesAdmin-filtro busca">
          <label>Busca geral</label>
          <div className="clientesAdmin-searchInput">
            <Search size={15} />
            <input
              name="busca"
              value={filtros.busca}
              onChange={handleFiltroChange}
              placeholder="Nome, e-mail, documento, ID, empresa..."
            />
          </div>
        </div>

        <div className="clientesAdmin-filtro">
          <label>Tipo</label>
          <select
            name="tipo_pessoa"
            value={filtros.tipo_pessoa}
            onChange={handleFiltroChange}
          >
            <option value="">Todos</option>
            <option value="fisica">Pessoa Física</option>
            <option value="juridica">Pessoa Jurídica</option>
          </select>
        </div>

        <div className="clientesAdmin-filtro">
          <label>Status</label>
          <select
            name="status"
            value={filtros.status}
            onChange={handleFiltroChange}
          >
            <option value="">Todos</option>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
          </select>
        </div>

        <div className="clientesAdmin-filtro">
          <label>Perfil</label>
          <select
            name="perfil"
            value={filtros.perfil}
            onChange={handleFiltroChange}
          >
            <option value="">Todos</option>
            <option value="confirmado">Confirmado</option>
            <option value="pendente">Pendente</option>
          </select>
        </div>

        <div className="clientesAdmin-filtro">
          <label>Documento</label>
          <select
            name="documento"
            value={filtros.documento}
            onChange={handleFiltroChange}
          >
            <option value="">Todos</option>
            <option value="validado">Validado</option>
            <option value="pendente">Pendente</option>
          </select>
        </div>

        <div className="clientesAdmin-filtro">
          <label>UF</label>
          <input
            name="uf"
            value={filtros.uf}
            onChange={handleFiltroChange}
            placeholder="UF"
            maxLength={2}
          />
        </div>
      </div>

      <div className="clientesAdmin-tableCard">
        <div className="clientesAdmin-tableHeader">
          <h3>Lista de clientes</h3>
          <span>
            {clientesFiltrados.length.toLocaleString("pt-BR")} resultado(s)
          </span>
        </div>

        <div className="clientesAdmin-tableWrapper">
          <table className="clientesAdmin-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Conta</th>
                <th>Documento</th>
                <th>Empresa</th>
                <th>Pontos</th>
                <th>Cadastro</th>
                <th>Status</th>
                <th>Auditoria</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="clientesAdmin-empty">
                    Carregando clientes...
                  </td>
                </tr>
              ) : clientesPagina.length === 0 ? (
                <tr>
                  <td colSpan="9" className="clientesAdmin-empty">
                    Nenhum cliente encontrado.
                  </td>
                </tr>
              ) : (
                clientesPagina.map((cliente) => {
                  const confirmado = isPerfilConfirmado(cliente);
                  const isPJ = cliente.tipo_pessoa === "juridica";

                  return (
                    <tr key={cliente.id}>
                      <td>
                        <div className="clientesAdmin-clienteCell">
                          <div className="clientesAdmin-avatar">
                            {cliente.nome?.charAt(0)?.toUpperCase() || "C"}
                          </div>

                          <div>
                            <strong>{cliente.nome || "—"}</strong>
                            <span>{cliente.email || "—"}</span>
                            <small>ID #{cliente.id}</small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="clientesAdmin-stack">
                          <span
                            className={`clientesAdmin-badge ${
                              isPJ ? "pj" : "pf"
                            }`}
                          >
                            {isPJ ? (
                              <Building2 size={12} />
                            ) : (
                              <UserRound size={12} />
                            )}
                            {isPJ ? "PJ" : "PF"}
                          </span>

                          {isPJ && cliente.identificador_conta_pj && (
                            <small>{cliente.identificador_conta_pj}</small>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="clientesAdmin-stack">
                          <strong>{cliente.cpf_cnpj || "Pendente"}</strong>

                          {Number(cliente.documento_validado) === 1 ? (
                            <span className="clientesAdmin-badge ok">
                              <BadgeCheck size={12} />
                              Validado
                            </span>
                          ) : (
                            <span className="clientesAdmin-badge warn">
                              <AlertTriangle size={12} />
                              Pendente
                            </span>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="clientesAdmin-stack empresa">
                          <strong>{cliente.nome_empresa || "—"}</strong>
                          <span>{cliente.cnpj_empresa || "—"}</span>
                          <small>
                            {[cliente.cidade, cliente.estado]
                              .filter(Boolean)
                              .join(" / ") || "—"}
                          </small>
                        </div>
                      </td>

                      <td>
                        <div className="clientesAdmin-stack pontos">
                          <strong>{formatarPontos(cliente.disponivel)} pts</strong>
                          <span>{cliente.perfil || "Start"}</span>
                        </div>
                      </td>

                      <td>{formatarData(cliente.data_cadastro)}</td>

                      <td>
                        <div className="clientesAdmin-stack">
                          <span
                            className={`clientesAdmin-badge status ${
                              cliente.status === "ativo" ? "ativo" : "inativo"
                            }`}
                          >
                            {cliente.status || "—"}
                          </span>

                          {confirmado ? (
                            <span className="clientesAdmin-badge ok">
                              <ShieldCheck size={12} />
                              Confirmado
                            </span>
                          ) : (
                            <span className="clientesAdmin-badge warn">
                              <FileClock size={12} />
                              Atualizar
                            </span>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="clientesAdmin-stack auditoria">
                          <strong>{cliente.total_logs || 0}</strong>
                          <span>log(s)</span>
                        </div>
                      </td>

                      <td>
                        {podeEditarClientes && (
                          <Link
                            to={`/clientes/editar/${cliente.id}`}
                            className="clientesAdmin-editBtn"
                          >
                            <Pencil size={14} />
                            Editar
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPaginas > 1 && (
        <div className="clientesAdmin-paginacao">
          <button
            onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
            disabled={paginaAtual === 1}
          >
            Anterior
          </button>

          <span>
            Página {paginaAtual} de {totalPaginas}
          </span>

          <button
            onClick={() =>
              setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))
            }
            disabled={paginaAtual === totalPaginas}
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
}

export default Clientes;